<template>
    <Container class="OverviewPanel">
        <h3>{{ $t('title') }}</h3>
        <div class="content-wrapper">
            <div class="left-container">
                <TheoryFeed
                    v-if="courseHasTheory"
                    :next-theory-journey-step="nextTheoryJoruneyStep"
                    :course-id="course.id"
                    :active-journey="activeJourney"
                />
                <PracticeFeed
                    v-if="courseHasPractice"
                    :current-scenario="currentScenario"
                    :lifeskill-id="lifeskillId"
                    :current-scenario-info="course.currentScenarioInfo"
                    :active-journey="activeJourney"
                    :course-id="course.id"
                    :did-user-scenario-today="didUserScenarioToday"
                />
                <SuggestedVideos :lifeskill-id="lifeskillId" />
            </div>
            <About :lifeskill-id="lifeskillId" />
        </div>
    </Container>
</template>

<translations>
    title: "Next tasks"
    title_no:  "Neste oppgaver"
    quizText: 'Complete quiz to get your lifekey'
    quizText_no: 'Ta reisequizen for å få livsnøkkelen'
    journeyQuiz: 'Journey quiz'
    journeyQuiz_no: 'Reisequiz'
    scenarioJourney: 'Scenario Journey {number}'
    scenarioJourney_no: 'Scenario reise  {number}'
    lockedPracticeText: 'Complete theory journey to unlock practice'
    lockedPracticeText_no: 'Fullfør teorireisen for å åpne praksis'
    unlockedPracticeText: 'Complete scenario journey to get your lifekey'
    unlockedPracticeText_no: 'Fullfør scenario reise for å få livsnøkkelen'
    theoryText: 'Complete journey to get your lifekey'
    theoryText_no: 'Fullfør ferdigheten for å få livsnøkkelen'
    dailyScenarioFinished: Daily scenario done. Come back tomorrow for the next one.
    dailyScenarioFinished_no: Ferdig med daglig scenario. Kom tilbake i morgen for det neste.
    doneStepsInformation: 'Steps {doneStepsCount}/{totalStepsCount}'
    doneStepsInformation_no: 'Steg {doneStepsCount}/{totalStepsCount}'
    doneScenariosInformation: 'Scenario {doneScenariosCount}/{totalScenariosCountInJourney}'
</translations>

<script>
import PracticeFeed from '../../components/PracticeFeed';
import TheoryFeed from '../../components/TheoryFeed';
import SuggestedVideos from '../../components/SuggestedVideos';
import About from '@/modules/dashboard/components/About';
import Container from '@/modules/dashboard/components/Container';

import { mapGetters, mapActions } from 'vuex';

export default {
    components: {
        TheoryFeed,
        PracticeFeed,
        SuggestedVideos,
        About,
        Container
    },

    props: {
        lifeskillId: {
            type: String,
            required: true,
            default: ''
        }
    },

    data() {
        return {
            excludedLifeskillIdsForTheory: ['10'],
            excludedLifeskillIdsForPractice: ['02'],
            scenariosDoneTimestamps: []
        };
    },

    computed: {
        ...mapGetters({
            getCourseStats: 'moduleCourse/getCourseStats',
            courseByLifeskillId: 'moduleCourse/courseByLifeskillId',
            getStepById: 'moduleCourse/getStepById',
            getJourneyById: 'moduleCourse/getJourneyById',
            getFirstStepFromJourney: 'moduleCourse/getFirstStepFromJourney',
            coursesInProgress: 'moduleApp/inProgressItems',
            getScenarioById: 'moduleCourse/getScenarioById'
        }),

        nextTheoryJoruneyStep() {
            const nextStepId = this.courseStats.firstActiveStepId;
            const firstJourney = this.getJourneyById(this.firstJourneyId);
            const firstJourneyStepId = firstJourney.stepIds[0];
            const isCourseInProgress = this.coursesInProgress.find(
                inProgress => inProgress.courseId === this.course.id
            );
            const isNextStepQuiz = isCourseInProgress && !nextStepId;

            const isQuizDoneInActiveJourney = this.activeJourney.quiz.done === 1;
            const userAcquiredWholeTheoryLifekeysInThisCourse =
                this.courseStats.acquiredTheoryLifekeys === this.courseStats.totalTheoryLifekeys;

            const nextJourneyIndex =
                !isQuizDoneInActiveJourney || userAcquiredWholeTheoryLifekeysInThisCourse
                    ? this.activeJourney.index + 1
                    : this.activeJourney.index + 2;

            if (isNextStepQuiz) {
                return {
                    title: this.$t('journeyQuiz'),
                    subtitle: this.activeTheoryJourneyDoneStepsInformation,
                    text: this.$t('quizText'),
                    isQuiz: true
                };
            } else {
                const text = this.$t('theoryText');
                const step = this.getStepById(nextStepId ? nextStepId : firstJourneyStepId);

                return {
                    stepId: step.id,
                    courseId: this.course.id,
                    title: step.title,
                    subtitle: this.activeTheoryJourneyDoneStepsInformation,
                    activities: step.activityNames,
                    text
                };
            }
        },

        activeTheoryJourneyDoneStepsInformation() {
            const doneStepsCount = this.activeJourney.stepIds.filter(stepId => {
                const { progress, isIntro } = this.getStepById(stepId);

                return progress === 1 && !isIntro;
            }).length;

            const totalStepsCount = this.activeJourney.stepIds.filter(stepId => {
                const { progress, isIntro } = this.getStepById(stepId);

                return !isIntro;
            }).length;

            return this.$t('doneStepsInformation', {
                doneStepsCount,
                totalStepsCount
            });
        },

        currentScenario() {
            const isCourseScenarioLocked = !this.course.currentScenarioInfo || this.didUserScenarioToday;
            const { name, index } = this.activeJourney;

            if (isCourseScenarioLocked) {
                return {
                    title: name ? name : this.$t('scenarioJourney', { number: index + 1 }),
                    subtitle: 'Scenario',
                    text: this.didUserScenarioToday ? this.$t('dailyScenarioFinished') : this.$t('lockedPracticeText'),
                    locked: true
                };
            } else {
                const currentScenarioInfo = this.course.currentScenarioInfo;
                const scenarioJourneyNumber = currentScenarioInfo.habitJourneyIndex + 1;
                const currentPracticeStep = this.getStepById(currentScenarioInfo.stepId);

                return {
                    title: currentPracticeStep.title,
                    subtitle: this.activePracticeJourneyDoneScenariosInformation,
                    text: this.$t('unlockedPracticeText'),
                    locked: false
                };
            }
        },

        activePracticeJourneyDoneScenariosInformation() {
            const currentScenarioInfo = this.course.currentScenarioInfo;
            const doneScenariosCount = this.scenariosDoneTimestamps.length;

            return this.$t('doneScenariosInformation', {
                doneScenariosCount,
                totalScenariosCountInJourney: this.totalScenariosCountInJourney
            });
        },

        totalScenariosCountInJourney() {
            const { stepGroupIds } = this.course;
            const activeScenarioIndex = this.course.currentScenarioInfo
                ? this.course.currentScenarioInfo.habitJourneyIndex
                : this.activeJouney.index;

            const courseJourneyStepGroups = stepGroupIds.map(stepGroupId => this.getJourneyById(stepGroupId).stepIds);
            const courseScenarioJourneys = courseJourneyStepGroups
                .map(stepGroup => stepGroup.map(stepId => this.getStepById(stepId)).filter(step => step.scenarioIds))
                .filter(scenarioJourney => scenarioJourney.length);

            return courseScenarioJourneys[activeScenarioIndex]
                .map(scenarioJourneyStep => scenarioJourneyStep.scenarioIds.length)
                .reduce((accumulator, currentValue) => accumulator + currentValue);
        },

        courseStats() {
            return this.getCourseStats(this.course.id);
        },

        firstJourneyId() {
            return this.course.stepGroupIds[0];
        },

        activeJourney() {
            const firstActiveJourneyId = this.courseStats.firstActiveJourneyId;

            return firstActiveJourneyId
                ? this.getJourneyById(firstActiveJourneyId)
                : this.getJourneyById(this.firstJourneyId);
        },

        course() {
            return this.courseByLifeskillId(this.lifeskillId);
        },

        courseHasTheory() {
            const courseIsNotOnExcludeList = !this.excludedLifeskillIdsForTheory.some(id => id === this.lifeskillId);

            return this.course && courseIsNotOnExcludeList;
        },

        courseHasPractice() {
            const courseHasScenarios = this.course && this.course.totalNumberOfScenarios;
            const courseIsNotOnExcludeList = !this.excludedLifeskillIdsForPractice.some(id => id === this.lifeskillId);

            return courseHasScenarios && courseIsNotOnExcludeList;
        },

        didUserScenarioToday() {
            return this.scenariosDoneTimestamps.some(timestamp => this.isToday(timestamp));
        },

        courseTimestamps() {
            return this.journeyStepsThatHasScenarios.map(stepGroup =>
                stepGroup.stepIds.map(stepId => {
                    const scenarioIds = this.getStepById(stepId).scenarioIds;

                    if (scenarioIds) {
                        return scenarioIds.map(scenarioId => this.getScenarioById(scenarioId).timestamp);
                    }
                })
            );
        },

        journeyStepsThatHasScenarios() {
            return this.courseStepGroups.filter(stepGroup =>
                stepGroup.stepIds.some(stepId => this.getStepById(stepId).scenarioIds)
            );
        },

        courseStepGroups() {
            return this.course.stepGroupIds.map(id => this.getJourneyById(id));
        }
    },

    created() {
        this.setCourseTimestamps();
    },

    methods: {
        setCourseTimestamps() {
            this.courseTimestamps.forEach(stepGroupTimestamps =>
                stepGroupTimestamps.forEach(stepTimestamps =>
                    stepTimestamps?.forEach(timestamp => timestamp && this.scenariosDoneTimestamps.push(timestamp))
                )
            );
        },

        isToday(timestamp) {
            const today = new Date();
            const scenarioDoneDate = new Date(parseInt(timestamp));

            return (
                scenarioDoneDate.getDate() === today.getDate() &&
                scenarioDoneDate.getMonth() === today.getMonth() &&
                scenarioDoneDate.getFullYear() === today.getFullYear()
            );
        }
    }
};
</script>

<style lang="scss" scoped>
.OverviewPanel {
    display: flex;
    align-items: center;
    flex-direction: column;
    background: #f7f7f7;
    margin: 0 1em;

    h3 {
        font-size: 22px;
        font-style: normal;
        font-weight: 400;
        line-height: 26px;
        color: #151515;
        margin-top: 54px;
        margin-bottom: 1.38em;
        text-align: left;
        width: 100%;
        margin-bottom: 22px;
        @media (max-width: 886px) {
            font-size: 18px;
            margin: 54px auto 22px;
        }
        @media (max-width: 530px) {
            font-size: 16px;
        }
    }
    .content-wrapper {
        display: flex;
        width: 100%;
        justify-content: space-between;
        @media (max-width: 884px) {
            flex-direction: column;
            align-items: center;
        }
        .left-container {
            width: 100%;
            margin-right: 30px;

            @media (max-width: 884px) {
                margin: 0;
            }
        }
    }
}
</style>
